/**
 * Competency Service
 *
 * Centralized service for handling competency data throughout the application.
 * Provides standardized functions, interfaces, and consistent error handling.
 */

import {
  CompetencyByGroupTypeEN,
  CompetencyByGroupTypeNL,
  CompetencyByGroupType
} from '../types/Assessment';

// ========== TypeScript Interfaces ==========

/**
 * Base competency interface representing competency data from the API
 */
export interface Competency {
  id: number;
  name: string;
  description?: string;
}

/**
 * Candidate's competency score
 */
export interface CandidateCompetency {
  competency_id: number;
  score: number;
  competency?: {
    name: string;
    description?: string;
  };
}

/**
 * Profile/job competency with minimum required score
 */
export interface ProfileCompetency {
  competency: {
    id: number;
    name: string;
    description?: string;
  };
  min_score: number;
}

/**
 * Format used in charts
 */
export interface CompetencyChartData {
  id?: number;
  label: string;
  point: number;
  descriptions?: string;
  comparePoint?: number | null;
}

/**
 * Legacy format for competency labels
 */
export type LegacyCompetencyLabels = string[];

/**
 * Modern format for competency labels with IDs
 */
export interface CompetencyLabel {
  id: number;
  name: string;
}

/**
 * Union type for competency labels
 */
export type CompetencyLabels = LegacyCompetencyLabels | CompetencyLabel[];

/**
 * Format for competency points in legacy format
 */
export type LegacyCompetencyPoints = number[];

/**
 * Format for competency points with IDs
 */
export interface CompetencyPoint {
  id: number;
  min_score: number;
}

/**
 * Union type for competency points
 */
export type CompetencyPoints = LegacyCompetencyPoints | CompetencyPoint[];

// ========== Helper Functions ==========

/**
 * Check if competency labels are in the modern format (with IDs)
 */
export const isModernFormat = (
  labels: CompetencyLabels
): labels is CompetencyLabel[] =>
  labels?.length > 0 &&
  typeof labels[0] !== 'string' &&
  labels[0]?.id !== undefined;

/**
 * Create a map of candidate competencies by ID for direct lookups
 */
export const createCompetencyMap = (
  candidateCompetencies: CandidateCompetency[] | any
): Record<number, CandidateCompetency> => {
  if (!Array.isArray(candidateCompetencies) || !candidateCompetencies.length)
    return {};

  const competencyMap: Record<number, CandidateCompetency> = {};

  candidateCompetencies.forEach((comp) => {
    if (comp && comp.competency_id) {
      // Check for duplicates with different scores
      if (
        competencyMap[comp.competency_id] &&
        competencyMap[comp.competency_id].score !== comp.score
      ) {
        console.warn(
          `Duplicate competency ID found: ${comp.competency_id} with different scores: ` +
            `${competencyMap[comp.competency_id].score} vs ${comp.score}`
        );
      }
      competencyMap[comp.competency_id] = comp;
    }
  });

  return competencyMap;
};

/**
 * Create a map of competencies by name (normalized) for fallback lookups
 */
export const createCompetencyNameMap = (
  candidateCompetencies: CandidateCompetency[] | any
): Record<string, CandidateCompetency> => {
  if (!Array.isArray(candidateCompetencies) || !candidateCompetencies.length)
    return {};

  const nameMap: Record<string, CandidateCompetency> = {};

  candidateCompetencies.forEach((comp) => {
    if (comp?.competency?.name) {
      const normalizedName = normalizeCompetencyName(comp.competency.name);

      // Check for duplicates with different scores
      if (
        nameMap[normalizedName] &&
        nameMap[normalizedName].score !== comp.score
      ) {
        console.warn(
          `Duplicate competency name found: "${comp.competency.name}" with different scores: ` +
            `${nameMap[normalizedName].score} vs ${comp.score}`
        );
      }

      nameMap[normalizedName] = comp;
    }
  });

  return nameMap;
};

/**
 * Normalize competency name for more reliable matching
 */
export const normalizeCompetencyName = (name: string): string => {
  if (!name || typeof name !== 'string') return '';
  return name.toLowerCase().trim();
};

/**
 * Check for duplicate competency names in profile competencies
 */
export const checkForDuplicateNames = (
  profileCompetencies: ProfileCompetency[] | any
): string[] => {
  if (!Array.isArray(profileCompetencies)) return [];

  const nameCount: Record<string, number> = {};
  const duplicates: string[] = [];

  profileCompetencies.forEach((comp) => {
    if (comp?.competency?.name) {
      const { name } = comp.competency;
      nameCount[name] = (nameCount[name] || 0) + 1;

      if (nameCount[name] === 2) {
        duplicates.push(name);
      }
    }
  });

  if (duplicates.length > 0) {
    console.warn('Duplicate competency names found:', duplicates);
  }

  return duplicates;
};

// ========== Main Service Functions ==========

/**
 * Match candidate competencies to profile competencies using ID-based lookups
 * Replaces the old getComparedData function with more robust ID-based matching
 */
export const matchCompetencies = (
  candidateCompetencies: CandidateCompetency[] | any,
  profileCompetencies: ProfileCompetency[] | any
): CandidateCompetency[] => {
  if (
    !Array.isArray(candidateCompetencies) ||
    !candidateCompetencies.length ||
    !Array.isArray(profileCompetencies) ||
    !profileCompetencies.length
  ) {
    return [];
  }

  // Check for duplicates in profile competencies
  checkForDuplicateNames(profileCompetencies);

  // Create ID and name maps for lookups
  const competencyMap = createCompetencyMap(candidateCompetencies);
  const nameMap = createCompetencyNameMap(candidateCompetencies);

  // Match profile competencies to candidate competencies
  return profileCompetencies
    .map((profileItem) => {
      const competencyId = profileItem?.competency?.id;
      const competencyName = profileItem?.competency?.name;

      let matchedComp: CandidateCompetency | null = null;

      // Always try ID-based match first (preferred method)
      if (competencyId && competencyMap[competencyId]) {
        matchedComp = competencyMap[competencyId];
      }
      // Fall back to name-based matching with our improved function
      else if (competencyName) {
        // First try exact name match
        const normalizedName = normalizeCompetencyName(competencyName);
        if (nameMap[normalizedName]) {
          matchedComp = nameMap[normalizedName];
        }
        // If no exact match, try flexible matching
        else {
          matchedComp = findCompetencyByName(
            candidateCompetencies,
            competencyName
          );
        }
      }

      return matchedComp;
    })
    .filter((comp): comp is CandidateCompetency => comp !== null); // Remove nulls with type predicate
};

/**
 * Create sorted points array for charts using ID-based matching
 */
export const createSortedPoints = (
  labels: CompetencyLabels,
  points: CompetencyPoints,
  candidateCompetencies: CandidateCompetency[] | any,
  descriptions?: any[]
): CompetencyChartData[] => {
  // Handle grouped assessment result format directly
  if (
    candidateCompetencies &&
    typeof candidateCompetencies === 'object' &&
    !Array.isArray(candidateCompetencies)
  ) {
    return createSortedPointsFromGroupedData(
      labels,
      points,
      candidateCompetencies,
      descriptions
    );
  }

  // Create competency map for lookups
  const competencyMap = createCompetencyMap(
    Array.isArray(candidateCompetencies) ? candidateCompetencies : []
  );

  // Build competency mappings from descriptions if available
  const competencyMappings: Record<number, string> = {};
  if (descriptions) {
    descriptions.forEach((desc: any) => {
      if (desc && desc.id && desc.name) {
        competencyMappings[desc.id] = desc.name;
      }
    });
  }

  // Handle modern format (with IDs)
  if (isModernFormat(labels)) {
    // Sort labels by ID for consistency
    const sortedLabels = [...labels].sort((a, b) => a.id - b.id);

    // Find matching points by ID and create chart data
    const sortedPoints = sortedLabels.map((label) => {
      // Find the matching point
      let pointValue = 0;
      if (Array.isArray(points) && typeof points[0] === 'number') {
        // Legacy points array
        pointValue = points[labels.indexOf(label)] as number;
      } else {
        // Modern format
        const matchedPoint = (points as CompetencyPoint[]).find(
          (p) => p.id === label.id
        );
        pointValue = matchedPoint?.min_score || 0;
      }

      // Look up candidate's competency score by ID
      const matchedComp = competencyMap[label.id];

      // Find description entry (also used for name fallback)
      const descEntry = descriptions?.find((d: any) => d.id === label.id);

      // Get label name - try label.name first, then fallback to descriptions array
      const labelName =
        label.name || descEntry?.name || competencyMappings[label.id] || '';

      // Parse score to number (it might be a string like "4.5")
      const scoreValue = matchedComp?.score;
      let comparePoint: number | null = null;
      if (scoreValue !== null && scoreValue !== undefined) {
        comparePoint =
          typeof scoreValue === 'string' ? parseFloat(scoreValue) : scoreValue;
      }

      return {
        point: pointValue,
        label: labelName,
        id: label.id,
        descriptions: descEntry?.description || '',
        comparePoint
      };
    });

    // Sort by point value (descending)
    return sortedPoints.sort((a, b) => (b.point || 0) - (a.point || 0));
  }

  // Handle legacy format (array of strings)
  // Also handle case where labels are objects with undefined id (fallback from isModernFormat check)
  return (labels as (string | { name?: string; id?: number })[])
    .map((label, i) => {
      // Extract label name - handle both string and object formats
      const labelName = typeof label === 'string' ? label : label?.name;

      // Try to find matching competency by name for legacy format
      let matchingComp = null;

      // Only attempt to find if candidateCompetencies is an array and we have a valid label name
      if (Array.isArray(candidateCompetencies) && labelName) {
        // Use our new flexible matching function
        matchingComp = findCompetencyByName(
          candidateCompetencies,
          labelName,
          competencyMappings
        );
      }

      // Find description if available
      const desc = descriptions?.find(
        (d: any) => d.name === labelName
      )?.description;

      // Parse score to number (it might be a string like "4.5")
      const scoreValue = matchingComp?.score;
      let comparePoint: number | null = null;
      if (scoreValue !== null && scoreValue !== undefined) {
        comparePoint =
          typeof scoreValue === 'string' ? parseFloat(scoreValue) : scoreValue;
      }

      return {
        point: Array.isArray(points) ? (points[i] as number) : 0,
        label: labelName || '',
        descriptions: desc || '',
        comparePoint
      };
    })
    .filter((item) => item.label) // Filter out items with empty labels
    .sort((a, b) => (b.point || 0) - (a.point || 0));
};

/**
 * Get competency scores and labels in a standardized format
 */
export const getCompetencyScoresAndLabels = (
  competencies: ProfileCompetency[]
): {
  competencies: ProfileCompetency[];
  competency_labels: { name: string; id: number }[];
  competency_min_scores: { min_score: number; id: number }[];
} => ({
  competencies,
  competency_labels: competencies?.length
    ? competencies.map((el) => ({
        name: el?.competency?.name,
        id: el?.competency?.id
      }))
    : [],
  competency_min_scores: competencies?.length
    ? competencies.map((el) => ({
        min_score: el?.min_score,
        id: el?.competency?.id
      }))
    : []
});

/**
 * Create sorted points directly from grouped assessment result data (more efficient)
 */
const createSortedPointsFromGroupedData = (
  labels: CompetencyLabels,
  points: CompetencyPoints,
  groupedData: CompetencyByGroupTypeEN | CompetencyByGroupTypeNL,
  descriptions?: any[]
): CompetencyChartData[] => {
  // Create a flat map of competency names to scores from grouped data
  const competencyScoreMap: Record<string, number> = {};

  Object.keys(groupedData).forEach((groupKey) => {
    const group = groupedData[
      groupKey as keyof typeof groupedData
    ] as CompetencyByGroupType;
    if (group && group.lables && group.data) {
      group.lables.forEach((label: string, index: number) => {
        competencyScoreMap[label] = group.data[index] || 0;
      });
    }
  });

  // Handle modern format (with IDs)
  if (isModernFormat(labels)) {
    const sortedLabels = [...labels].sort((a, b) => a.id - b.id);

    return sortedLabels
      .map((label) => {
        // Find the matching point
        let pointValue = 0;
        if (Array.isArray(points) && typeof points[0] === 'number') {
          pointValue = points[labels.indexOf(label)] as number;
        } else {
          const matchedPoint = (points as CompetencyPoint[]).find(
            (p) => p.id === label.id
          );
          pointValue = matchedPoint?.min_score || 0;
        }

        // Find description if available
        const desc = descriptions?.find(
          (d: any) => d.id === label.id || d.name === label.name
        )?.description;

        return {
          point: pointValue,
          label: label.name,
          id: label.id,
          descriptions: desc || '',
          comparePoint: competencyScoreMap[label.name] || null
        };
      })
      .sort((a, b) => (b.point || 0) - (a.point || 0));
  }

  // Handle legacy format (array of strings)
  return labels
    .map((label, i) => {
      // Find description if available
      const desc = descriptions?.find(
        (d: any) => d.name === label
      )?.description;

      return {
        point: Array.isArray(points) ? (points[i] as number) : 0,
        label,
        descriptions: desc || '',
        comparePoint: competencyScoreMap[label] || null
      };
    })
    .sort((a, b) => (b.point || 0) - (a.point || 0));
};

/**
 * Find a competency by name with flexible matching
 */
export const findCompetencyByName = (
  candidateCompetencies: CandidateCompetency[] | any,
  searchName: string,
  competencyMappings?: Record<number, string>
): CandidateCompetency | null => {
  if (
    !Array.isArray(candidateCompetencies) ||
    !candidateCompetencies.length ||
    !searchName
  )
    return null;

  const normalizedSearch = normalizeCompetencyName(searchName);

  // First try exact match if competency objects have names
  const exactMatch = candidateCompetencies.find(
    (c) =>
      c?.competency?.name &&
      normalizeCompetencyName(c.competency.name) === normalizedSearch
  );

  if (exactMatch) return exactMatch;

  // If we have competency mappings, try to match by ID
  if (competencyMappings) {
    // Find competency ID that maps to this name
    const matchingId = Object.entries(competencyMappings).find(
      ([, name]) => normalizeCompetencyName(name as string) === normalizedSearch
    )?.[0];

    if (matchingId) {
      const idMatch = candidateCompetencies.find(
        (c) => c.competency_id === parseInt(matchingId, 10)
      );

      if (idMatch) return idMatch;
    }
  }

  // Then try partial matches if competency objects have names
  const partialMatch = candidateCompetencies.find((c) => {
    if (!c?.competency?.name) return false;

    const normalizedName = normalizeCompetencyName(c.competency.name);
    return (
      normalizedName.includes(normalizedSearch) ||
      normalizedSearch.includes(normalizedName)
    );
  });

  if (partialMatch) {
    return partialMatch;
  }

  // If no matches found and competencies don't have names, we can't match by name
  return null;
};

// Export the old function name for backward compatibility
export const getComparedData = matchCompetencies;

/**
 * Extract competency ID from various data formats
 * Supports: { competency: { id } }, { competency: X }, { competency_id: X }
 */
export const extractCompetencyId = (el: any): number | undefined => {
  if (el?.competency?.id !== undefined) return el.competency.id;
  if (el?.competency_id !== undefined) return el.competency_id;
  if (typeof el?.competency === 'number') return el.competency;
  if (typeof el?.id === 'number') return el.id;
  return undefined;
};

/**
 * Extract competency name from various data formats
 * Supports: { competency: { name } }, { name: X }
 */
export const extractCompetencyName = (el: any): string | undefined => {
  if (typeof el?.competency?.name === 'string') return el.competency.name;
  if (typeof el?.name === 'string') return el.name;
  return undefined;
};

/**
 * Extract competency description from various data formats
 */
export const extractCompetencyDescription = (el: any): string | undefined => {
  if (typeof el?.competency?.description === 'string')
    return el.competency.description;
  if (typeof el?.description === 'string') return el.description;
  return undefined;
};

/**
 * Safely extract competency info from any format, with optional name lookup
 */
export const safeExtractCompetencyInfo = (
  el: any,
  nameLookup?: Record<number, string>,
  descLookup?: Record<number, string>
): { id?: number; name?: string; description?: string } => {
  const id = extractCompetencyId(el);
  let name = extractCompetencyName(el);
  let description = extractCompetencyDescription(el);

  // Try to resolve from lookup if not available
  if (id !== undefined) {
    if (!name && nameLookup?.[id]) {
      name = nameLookup[id];
    }
    if (!description && descLookup?.[id]) {
      description = descLookup[id];
    }
  }

  return { id, name, description };
};

/**
 * Build a mapping of competency IDs to names from job post data
 * Handles multiple data formats
 */
export const buildCompetencyMappings = (
  jobPostCompetencies: any[]
): Record<number, string> => {
  const mappings: Record<number, string> = {};

  if (!Array.isArray(jobPostCompetencies)) return mappings;

  jobPostCompetencies.forEach((comp) => {
    const id = extractCompetencyId(comp);
    const name = extractCompetencyName(comp);
    if (id !== undefined && name) {
      mappings[id] = name;
    }
  });

  return mappings;
};

/**
 * Build a mapping of competency IDs to names from grouped competencies data
 * (from /api/assessment/competencies_by_group/)
 */
export const buildCompetencyMappingsFromGroups = (
  groupedCompetencies: any[]
): Record<number, string> => {
  const mappings: Record<number, string> = {};

  if (!Array.isArray(groupedCompetencies)) return mappings;

  groupedCompetencies.forEach((group) => {
    if (Array.isArray(group?.competencies)) {
      group.competencies.forEach((comp: any) => {
        if (comp?.id && comp?.name) {
          mappings[comp.id] = comp.name;
        }
      });
    }
  });

  return mappings;
};

/**
 * Build a mapping of competency IDs to descriptions from grouped competencies data
 */
export const buildCompetencyDescriptionMappings = (
  groupedCompetencies: any[]
): Record<number, string> => {
  const mappings: Record<number, string> = {};

  if (!Array.isArray(groupedCompetencies)) return mappings;

  groupedCompetencies.forEach((group) => {
    if (Array.isArray(group?.competencies)) {
      group.competencies.forEach((comp: any) => {
        if (comp?.id && comp?.description) {
          mappings[comp.id] = comp.description;
        }
      });
    }
  });

  return mappings;
};
